import {
  type GetPaginationSessionsParams,
  type SessionsPresenter,
  useInfiniteScrollFromCache,
  useLazyGetSessionsPresentersQuery,
} from '@carboncopyco/data';
import { Avatar, TextBase, stringToColor } from '@carboncopyco/ui';
import {
  getDateFromDaysAgo,
  getNowInFormat,
  getTimezoneName,
  useDashboardAnalytics,
} from '@carboncopyco/utils';
import { useEffect, useMemo } from 'react';
import { SearchDropdown } from '../../../SearchDropdown';
import { useDashboardProps } from '../../DashboardContext';
import { checkboxClasses } from '../../DashboardHeader/WorkspaceFilter';
import { useTableProps } from '../../DemoDashboard/SessionBreakdownTables/TableContext';
import { useFilterLogic } from './useFilterLogic';

export const PresentersFilter = () => {
  const { organizationId, selectedWorkspaceIds, timeRange, short, internal } =
    useDashboardProps();
  const { presenters, setPresenters } = useTableProps();
  const { trackDashboardSessionsBreakdownPresenterFilterClick } =
    useDashboardAnalytics();

  const fetchArgs = useMemo<GetPaginationSessionsParams>(
    () => ({
      organizationId,
      shortSessions: short,
      internalSessions: internal,
      workspaceIds: selectedWorkspaceIds,
      start: getDateFromDaysAgo(timeRange),
      end: getNowInFormat('YYYY-MM-DDTHH:mm:ssZ'),
      timezone: getTimezoneName(),
      resourceTypes: ['managed_presentation', 'play'],
    }),
    [organizationId, short, internal, selectedWorkspaceIds, timeRange]
  );

  useEffect(() => {
    fetchFirstBatch();
  }, [fetchArgs]);

  const {
    items: data,
    isLoading,
    isLoadingBatch,
    fetchFirstBatch,
    fetchNextBatch,
    isEmpty,
  } = useInfiniteScrollFromCache<
    SessionsPresenter,
    GetPaginationSessionsParams
  >(useLazyGetSessionsPresentersQuery, fetchArgs);

  const [items, onChange] = useFilterLogic({
    data,
    selectedItems: presenters,
    id: 'email',
    label: 'name',
    setter: setPresenters,
  });

  const renderPresenterItem = ({ checked, onChange, item }) => {
    return (
      <div className="px-2 h-9 cursor-pointer flex items-center w-full gap-2 focus:bg-ds-natural-50 focus:outline-none focus:rounded-md">
        <div className="flex items-center gap-3">
          <input
            checked={checked}
            onChange={onChange}
            type="checkbox"
            className={checkboxClasses(checked)}
          />
          <div className="flex items-center gap-2 truncate">
            <Avatar
              backgroundColor={stringToColor(item.label)}
              image={item.image}
              size="small"
              initials={item.label
                .split(' ')
                .map((word) => word[0])
                .join('')}
            />
            <div className="truncate max-w-[120px]">
              <TextBase
                color="text-ds-natural-600"
                label={item.label}
                ellipsis
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <SearchDropdown
      placeholder="Presenter"
      capsuleWidth={208}
      contentWidth={208}
      fixedItems={items}
      selectedItems={presenters}
      onChange={(item) => {
        onChange(item);
        trackDashboardSessionsBreakdownPresenterFilterClick({});
      }}
      onClear={() => setPresenters({})}
      onInputChange={fetchFirstBatch}
      fetchNextBatch={fetchNextBatch}
      isLoading={isLoading}
      isLoadingBatch={isLoadingBatch}
      renderItem={renderPresenterItem}
      disabled={isEmpty}
    />
  );
};
